import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { UpdateVisitasEstadoDto, VisitasDto } from '../../../types/visitas';

interface VisitaEstadoDialogProps {
  open: boolean;
  action: 'iniciar' | 'completar' | 'omitir';
  visita: VisitasDto | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: UpdateVisitasEstadoDto) => Promise<void>;
}

const titleByAction: Record<'iniciar' | 'completar' | 'omitir', string> = {
  iniciar: 'Iniciar visita',
  completar: 'Completar visita',
  omitir: 'Omitir visita',
};

const VisitaEstadoDialog: React.FC<VisitaEstadoDialogProps> = ({
  open,
  action,
  visita,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [motivoOmitida, setMotivoOmitida] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setMotivoOmitida('');
    setObservaciones(visita?.observaciones ?? '');
    setSubmitting(false);
  }, [open, visita]);

  const isOmitir = action === 'omitir';
  const busy = saving || submitting;
  const motivoOk = !isOmitir || motivoOmitida.trim().length > 0;
  const canSubmit = useMemo(() => !busy && motivoOk, [busy, motivoOk]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      await onSubmit({
        motivoOmitida: isOmitir ? motivoOmitida.trim() || null : null,
        observaciones: observaciones.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{titleByAction[action]}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Visita #{visita?.idVisita} - Cliente {visita?.nroCliente}
        </Typography>
        {isOmitir && (
          <TextField
            fullWidth
            required
            label="Motivo de omisión"
            value={motivoOmitida}
            onChange={(e) => setMotivoOmitida(e.target.value)}
            disabled={busy}
            error={motivoOmitida.trim().length === 0}
            helperText={
              motivoOmitida.trim().length === 0
                ? 'Ingresá un motivo para poder confirmar'
                : undefined
            }
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {busy ? 'Guardando…' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitaEstadoDialog;
